import React from "react";
import { cn } from "../../../lib/utils";
import { formatTimestamp, formatDuration } from "../utils/gatewayUtils";
import GatewayStatusBadge from "./GatewayStatusBadge";
import GatewayModuleBadge from "./GatewayModuleBadge";

interface TimelineEvent {
  id?: string;
  timestamp: string;
  eventType?: string;
  module?: string;
  status: string;
  duration?: number;
  capabilityName?: string;
  serverConfigName?: string;
  correlationId?: string;
  errorMessage?: string;
}

interface GatewayTimelineProps {
  events: TimelineEvent[];
  className?: string;
}

export default function GatewayTimeline({ events, className }: GatewayTimelineProps) {
  if (!events || events.length === 0) {
    return <div className="text-sm text-muted-foreground text-center py-4">No events</div>;
  }

  return (
    <div className={cn("relative", className)}>
      {events.map((event, i) => {
        const isLast = i === events.length - 1;
        const statusCls = event.status === "SUCCESS" || event.status === "COMPLETED"
          ? "bg-emerald-500"
          : event.status === "ERROR" || event.status === "FAILURE"
            ? "bg-red-500"
            : event.status === "DENIED"
              ? "bg-orange-500"
              : "bg-primary";

        return (
          <div key={event.id || i} className="relative flex gap-3 pb-0">
            <div className="flex flex-col items-center shrink-0 w-4">
              <div className={cn("w-2.5 h-2.5 rounded-full mt-2.5 shrink-0 z-10", statusCls)} />
              {!isLast && (
                <div className="w-[2px] flex-1 bg-muted-foreground/25 min-h-[28px]" />
              )}
            </div>

            <div className="flex-1 min-w-0 pb-4 grid grid-cols-[1fr_70px_80px_50px_120px] items-start gap-3">
              <div className="min-w-0">
                <span className="text-xs text-foreground/80">{event.eventType || "Event"}</span>
                {event.correlationId && (
                  <span className="text-[10px] text-muted-foreground/50 font-mono ml-2">
                    ID: {event.correlationId.slice(0, 8)}
                  </span>
                )}
                {event.errorMessage && (
                  <div className="text-[10px] text-red-400 truncate mt-0.5">{event.errorMessage}</div>
                )}
              </div>

              <div className="flex justify-end">
                <GatewayStatusBadge status={event.status} />
              </div>

              <div className="flex justify-end">
                {event.module ? <GatewayModuleBadge module={event.module} /> : null}
              </div>

              <span className="text-[10px] text-muted-foreground font-mono text-right">
                {event.duration != null ? formatDuration(event.duration) : ""}
              </span>

              <span className="text-[10px] text-muted-foreground/60 font-mono text-right">
                {formatTimestamp(event.timestamp)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
